import { z } from "zod";
import { InvalidOrderSetError } from "../errors.model";
import {
  CURRENT_ORDER_SET_STATUSES,
  type CurrentOrderSetStatus,
  type OrderSetChange,
  type OrderSetDraftBaseline,
  type OrderSetSnapshot,
} from "../order-set.model";

/** A set still on its plan: being curated, or ready to book. */
export function isCurrentOrderSetStatus(status: string): status is CurrentOrderSetStatus {
  return (CURRENT_ORDER_SET_STATUSES as readonly string[]).includes(status);
}

// Shape only: the aggregate validates the baseline's content when it hydrates.
const draftBaselineSchema = z.object({
  envelopeMinutes: z.number().int().nonnegative(),
  steps: z.array(
    z
      .object({
        id: z.string(),
        placement: z.record(z.unknown()),
        orders: z.array(z.object({ orderId: z.string() }).passthrough()),
      })
      .passthrough(),
  ),
});

export function encodeDraftBaseline(baseline: OrderSetDraftBaseline): string {
  return JSON.stringify(baseline);
}

export function decodeDraftBaseline(encoded: string): OrderSetDraftBaseline {
  let json: unknown;
  try {
    json = JSON.parse(encoded);
  } catch {
    throw new InvalidOrderSetError("An order set draft baseline is not JSON");
  }
  const parsed = draftBaselineSchema.safeParse(json);
  if (!parsed.success) throw new InvalidOrderSetError("An order set draft baseline has the wrong shape");
  return parsed.data as unknown as OrderSetDraftBaseline;
}

export function draftBaselineOf({ envelopeMinutes, steps }: OrderSetSnapshot): OrderSetDraftBaseline {
  return structuredClone({ envelopeMinutes, steps });
}

/**
 * The baseline put back over the current set. Each order keeps its current
 * parameter answers: answers are saved as given, never staged with a draft.
 */
export function restoreDraftBaseline(
  current: OrderSetSnapshot,
  baseline: OrderSetDraftBaseline,
): Omit<OrderSetSnapshot, "draftBaseline"> {
  const { draftBaseline: _replaced, ...rest } = current;
  const answers = new Map(
    current.steps.flatMap((step) => step.orders.map((order) => [order.orderId, order.parameterValues] as const)),
  );
  return {
    ...rest,
    status: "active",
    envelopeMinutes: baseline.envelopeMinutes,
    steps: baseline.steps.map((step) => ({
      ...step,
      orders: step.orders.map((order) => ({
        ...order,
        parameterValues: structuredClone(answers.get(order.orderId) ?? order.parameterValues),
      })),
    })),
  };
}

/**
 * The log drains per commit and a draft spans many, so draft edits are held
 * back as they happen and logged once as `DraftSaved`. An answer to an order
 * the baseline holds is the exception: Discard keeps it, so it is logged now.
 */
export function logsWhileDrafting(change: OrderSetChange, baseline: OrderSetDraftBaseline | undefined): boolean {
  return (
    change.kind === "ParameterChanged" &&
    baseline !== undefined &&
    baseline.steps.some((step) => step.orders.some((order) => order.orderId === change.orderId))
  );
}
